"use client";

import { Html, If, SmartImage } from "@/Components";
import { Col, Row, theme, Typography } from "antd";
import classNames from "classnames";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import Slider, { Settings as SlickSettings } from "react-slick";

const { Title, Text } = Typography;

export type TabBase = { Id: number; Name: string; ItemsCount: number };
export type ItemBase = { Id: number };

export interface TabbedStoreSectionProps<
  TTab extends TabBase,
  TItem extends ItemBase,
> {
  // header
  title?: string;
  description?: string;

  // background
  showBackgroundImage?: boolean;
  backgroundImageSrc?: string;

  // tabs & items
  tabs: TTab[];
  items: TItem[];
  getTabId?: (t: TTab) => number; // default: t.Id
  getItemId?: (i: TItem) => number; // default: i.Id
  getItemTabId: (i: TItem) => number;

  // display mode
  displayModeKey?: string;
  sliderModeKey: string;

  // renderers
  renderTabText?: (tab: TTab, isActive: boolean, count: number) => ReactNode;
  renderItem: (item: TItem) => ReactNode;

  // optional per-tab footer (e.g. View All)
  renderTabFooter?: (tab: TTab) => ReactNode;

  // layout
  gridColProps?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  sliderSettings?: SlickSettings;

  // initial
  initialActiveTabId?: number;
  autoSelectDefaultTab?: boolean;

  /** فقط تب‌هایی که آیتم دارند نمایش داده شوند */
  hideEmptyTabs?: boolean;
}

const defaultSliderSettings: SlickSettings = {
  slidesToShow: 1,
  slidesToScroll: 1,
  dots: true,
  arrows: false,
  infinite: true,
  focusOnSelect: true,
  responsive: [
    { breakpoint: 576, settings: { slidesToShow: 1 } }, // گوشی
    { breakpoint: 768, settings: { slidesToShow: 2 } }, // تبلت کوچک
    { breakpoint: 992, settings: { slidesToShow: 3 } }, // تبلت/لپتاپ
    { breakpoint: 1200, settings: { slidesToShow: 4 } }, // دسکتاپ
  ],
};

const TabbedStoreSection = <TTab extends TabBase, TItem extends ItemBase>(
  props: TabbedStoreSectionProps<TTab, TItem>,
) => {
  const {
    title,
    description,
    showBackgroundImage = true,
    backgroundImageSrc = "/Templates/Doccure/img/bg/our-store-bg.png",
    tabs,
    items,
    getTabId = (t) => t.Id,
    getItemId = (i) => i.Id,
    getItemTabId,
    displayModeKey,
    sliderModeKey,
    renderTabText,
    renderItem,
    renderTabFooter,
    gridColProps = { xs: 24, md: 6 },
    sliderSettings = defaultSliderSettings,
    initialActiveTabId,
    autoSelectDefaultTab = true,
    hideEmptyTabs = false,
  } = props;

  const { token } = theme.useToken();

  const filteredTabs = useMemo(() => {
    if (!hideEmptyTabs) return tabs;

    return tabs.filter((tab) => tab.ItemsCount > 0);
  }, [tabs, items, hideEmptyTabs, getTabId, getItemTabId]);

  const firstTabId = useMemo(() => {
    const first = filteredTabs?.[0];
    return first ? getTabId(first) : undefined;
  }, [filteredTabs, getTabId]);

  const [activeTabId, setActiveTabId] = useState<number | undefined>(
    initialActiveTabId ?? firstTabId,
  );

  useEffect(() => {
    if (!autoSelectDefaultTab) {
      return;
    }

    if (filteredTabs.length === 0) {
      if (activeTabId !== undefined) {
        setActiveTabId(undefined);
      }

      return;
    }

    const hasActiveTab =
      activeTabId !== undefined &&
      filteredTabs.some((tab) => getTabId(tab) === activeTabId);

    if (hasActiveTab) {
      return;
    }

    const hasInitialTab =
      initialActiveTabId !== undefined &&
      filteredTabs.some((tab) => getTabId(tab) === initialActiveTabId);

    setActiveTabId(hasInitialTab ? initialActiveTabId : firstTabId);
  }, [
    activeTabId,
    autoSelectDefaultTab,
    filteredTabs,
    firstTabId,
    getTabId,
    initialActiveTabId,
  ]);

  const activeItems = useMemo(() => {
    if (!activeTabId) return [];
    return items.filter((x) => getItemTabId(x) === activeTabId);
  }, [items, activeTabId, getItemTabId]);

  const content = useMemo(() => {
    if (displayModeKey === sliderModeKey) {
      return (
        <Slider {...sliderSettings}>
          {activeItems.map((item) => (
            <React.Fragment key={getItemId(item)}>
              {renderItem(item)}
            </React.Fragment>
          ))}
        </Slider>
      );
    }

    return (
      <Row gutter={[8, 8]}>
        {activeItems.map((item) => (
          <Col key={getItemId(item)} {...gridColProps}>
            {renderItem(item)}
          </Col>
        ))}
      </Row>
    );
  }, [
    activeItems,
    displayModeKey,
    sliderModeKey,
    sliderSettings,
    gridColProps,
    getItemId,
    renderItem,
  ]);

  return (
    <section className="store-section">
      <If condition={showBackgroundImage}>
        <div className="sec-bg-img">
          <SmartImage
            width={381}
            height={713}
            src={backgroundImageSrc}
            alt="Img"
          />
        </div>
      </If>

      <div className="container">
        <div className="row">
          <div className="col-md-12 aos" data-aos="fade-up">
            <If condition={!!title && !!description}>
              <div className="section-heading text-center sec-heading-eye">
                <Title level={2}>{title}</Title>
                <p>
                  <Html value={description} />
                </p>
              </div>
            </If>

            <ul className="store-tab nav">
              {filteredTabs.map((tab) => {
                const tabId = getTabId(tab);
                const isActive = tabId === activeTabId;

                return (
                  <li key={tabId}>
                    <button
                      className={classNames({ active: isActive })}
                      onClick={() => setActiveTabId(tabId)}
                    >
                      {renderTabText ? (
                        renderTabText(tab, isActive, tab.ItemsCount)
                      ) : (
                        <Text
                          style={{
                            color: isActive
                              ? token.colorPrimary
                              : token.colorTextSecondary,
                            fontWeight: isActive ? 600 : undefined,
                          }}
                        >
                          {tab.ItemsCount
                            ? `${tab.Name} (${tab.ItemsCount})`
                            : tab.Name}
                        </Text>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="tab-content">
          {filteredTabs.map((tab) => {
            const tabId = getTabId(tab);
            const isActive = tabId === activeTabId;

            return (
              <div
                key={tabId}
                className={classNames("tab-pane", {
                  active: isActive,
                  fade: !isActive,
                })}
              >
                <div
                  className="eye-blogslider eye-dots slick-margins-15 owl-them aos"
                  data-aos="fade-up"
                >
                  {content}
                  {renderTabFooter?.(tab)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TabbedStoreSection;
